import { s as packCellKey } from "../cell-key-COqbCX2L.js";
import { t as meshOccupiedCells } from "../occupancy-mesher-gHZMBiIc.js";
//#region ../src/ar/occlusion-mesh-worker.ts
/** Only the surface-hugging modes read `getCellPoint`. */
function needsCentroids(mode) {
	return mode === "smooth" || mode === "corner-fit";
}
/**
* Pack an occupied-cell snapshot into a transferable {@link MeshWorkerRequest}
* (main thread). `transfer` is the list to pass as the second `postMessage` arg
* so the buffers move (not copy) to the worker.
*
* `cells` may be the classic tuple array or an already-flat
* `[x0,y0,z0, x1,y1,z1, …]` Int32Array (Step 1.3 of the 2026-07-03
* long-session fps plan — `OccupancyGrid.getOccupiedCellsFlat` hands the
* snapshot over flat, deleting the tuple intermediate this function used to
* re-flatten). A flat snapshot is used **zero-copy**: its buffer lands in
* `transfer` and is DETACHED after posting — callers must pass a fresh array
* they do not reuse. Throws `RangeError` when a flat snapshot's length is not
* a multiple of 3 (a truncated buffer must fail loudly, not mesh garbage).
*
* `getCellPoint` receives a **transient** cell tuple (a reused scratch on the
* flat path — see `MeshOccupiedCellsOptions.getCellPoint` for the contract):
* implementations must not retain the tuple beyond the call.
*/
function packMeshRequest(id, cells, cellSizeM, mode, getCellPoint) {
	let flat;
	let n;
	if (cells instanceof Int32Array) {
		if (cells.length % 3 !== 0) throw new RangeError(`flat cell snapshot length must be a multiple of 3, got ${cells.length}`);
		flat = cells;
		n = cells.length / 3;
	} else {
		n = cells.length;
		flat = new Int32Array(n * 3);
		for (let i = 0; i < n; i++) {
			const c = cells[i];
			flat[i * 3] = c[0];
			flat[i * 3 + 1] = c[1];
			flat[i * 3 + 2] = c[2];
		}
	}
	let centroids = null;
	if (needsCentroids(mode) && getCellPoint) {
		centroids = new Float64Array(n * 3);
		const scratch = [
			0,
			0,
			0
		];
		for (let i = 0; i < n; i++) {
			let cell;
			if (cells instanceof Int32Array) {
				scratch[0] = flat[i * 3];
				scratch[1] = flat[i * 3 + 1];
				scratch[2] = flat[i * 3 + 2];
				cell = scratch;
			} else cell = cells[i];
			const cp = getCellPoint(cell);
			if (cp) {
				centroids[i * 3] = cp[0];
				centroids[i * 3 + 1] = cp[1];
				centroids[i * 3 + 2] = cp[2];
			} else {
				centroids[i * 3] = NaN;
				centroids[i * 3 + 1] = NaN;
				centroids[i * 3 + 2] = NaN;
			}
		}
	}
	const request = {
		id,
		cells: flat,
		cellSizeM,
		mode,
		centroids
	};
	const transfer = [flat.buffer];
	if (centroids) transfer.push(centroids.buffer);
	return {
		request,
		transfer
	};
}
/**
* Mesh a {@link MeshWorkerRequest} (worker side). Returns the geometry plus the
* transfer list for posting back. Byte-identical to a direct `meshOccupiedCells`.
*/
function runMeshRequest(request) {
	const { id, cells: flat, cellSizeM, mode, centroids } = request;
	const n = flat.length / 3;
	const cells = [];
	for (let i = 0; i < n; i++) cells.push([
		flat[i * 3],
		flat[i * 3 + 1],
		flat[i * 3 + 2]
	]);
	let getCellPoint;
	const pts = centroids;
	if (pts && mode !== "smooth") {
		const indexByKey = /* @__PURE__ */ new Map();
		for (let i = 0; i < n; i++) indexByKey.set(packCellKey(flat[i * 3], flat[i * 3 + 1], flat[i * 3 + 2]), i);
		getCellPoint = (cell) => {
			const i = indexByKey.get(packCellKey(cell[0], cell[1], cell[2]));
			if (i === void 0) return null;
			const cx = pts[i * 3];
			if (Number.isNaN(cx)) return null;
			return [
				cx,
				pts[i * 3 + 1],
				pts[i * 3 + 2]
			];
		};
	}
	const mesh = meshOccupiedCells(cells, cellSizeM, {
		mode,
		getCellPoint,
		centroids,
		includeAabbs: false
	});
	return {
		response: {
			id,
			positions: mesh.positions,
			indices: mesh.indices
		},
		transfer: [mesh.positions.buffer, mesh.indices.buffer]
	};
}
//#endregion
export { packMeshRequest, runMeshRequest };
